/**
 * Updates the user authentication service with the provided token and cleans the token from the URL.
 * @param token - The token to set in the user authentication service.
 * @param location - The location object from the router.
 * @param userAuthenticationService - The user authentication service instance.
 */
export const INCOMING_AUTH_TOKEN_KEY = 'incoming_auth_token';
const AUTH_WARNING_EVENT = 'vidaa-auth-warning';

type RouteLocation = {
  search: string;
};

type UserAuthenticationServiceLike = {
  setServiceImplementation: (implementation: {
    getAuthorizationHeader: () => { Authorization: string };
  }) => void;
};

function normalizeIncomingToken(rawToken: string | null) {
  if (!rawToken) {
    return null;
  }

  const token = `${rawToken}`.trim();

  if (!token || token === 'undefined' || token === 'null') {
    return null;
  }

  return token;
}

function hasIncomingToken() {
  return Boolean(normalizeIncomingToken(sessionStorage.getItem(INCOMING_AUTH_TOKEN_KEY)));
}

function removeTokenFromUrl(location: RouteLocation) {
  const urlObj = new URL(window.location.origin + window.location.pathname + location.search);
  urlObj.searchParams.delete('token');

  if (window.history?.replaceState) {
    window.history.replaceState(null, '', urlObj.toString());
  }
}

export function updateAuthServiceAndCleanUrl(
  token: string,
  location: RouteLocation,
  userAuthenticationService: UserAuthenticationServiceLike
): void {
  const normalizedToken = normalizeIncomingToken(token);

  if (!normalizedToken) {
    sessionStorage.removeItem(INCOMING_AUTH_TOKEN_KEY);

    // Always remove malformed token values from URL to avoid repeated warnings.
    removeTokenFromUrl(location);

    const persistedViewerToken = normalizeIncomingToken(sessionStorage.getItem('session_viewer'));
    if (persistedViewerToken) {
      return;
    }

    // Keep the viewer in place; bootstrap/auth flows decide next steps explicitly.

    window.dispatchEvent(
      new CustomEvent(AUTH_WARNING_EVENT, {
        detail: {
          message:
            'No auth token was found in URL. Automatic redirect is disabled; review the access link or login flow.',
        },
      })
    );

    return;
  }

  // Persist the incoming token synchronously so later async effects can reuse it.
  sessionStorage.setItem(INCOMING_AUTH_TOKEN_KEY, normalizedToken);

  // if a token is passed in, set the userAuthenticationService to use it
  // for the Authorization header for all requests
  userAuthenticationService.setServiceImplementation({
    getAuthorizationHeader: () => ({
      Authorization: 'Basic ' + normalizedToken,
    }),
  });

  // Avoid cleaning the URL if the token was not persisted for any reason.
  if (!hasIncomingToken()) {
    return;
  }

  removeTokenFromUrl(location);
}
